<template>
  <Dialog :visible="visible" header="Importa bollettini (txt) [new]" :modal="true" :style="{ width: '85%' }"
    :closeOnEscape="false" class="p-fluid" @update:visible="chiudi" @show="caricaAssociazioni" @hide="reset">
    <Loader v-if="loading" style="z-index: 2000" />

    <div v-if="!verifica" class="formgrid grid">
      <div class="field col-12">Tipo di pagamento: <b>{{ tipoPagamento }}</b></div>
      <div class="field col-12">
        <input type="file" accept="text/plain" @change="scegliFile" />
      </div>

      <div class="field col-12 mt-3">
        <h5 class="mb-2">Ultime associazioni</h5>
        <Message severity="info" :closable="false">
          "Annulla associazione" cancella i pagamenti registrati e rimette accrediti, ricevute, movimenti ignoti e
          saldi delle fatture com'erano prima; il saldo di una fattura che nel frattempo ha avuto altri pagamenti viene
          ricalcolato. Se qualcosa scritto dall'associazione è cambiato dopo (un pagamento cancellato o già esportato in
          contabilità, un accredito dissociato) non viene annullato niente. Dopo l'annullamento il txt si può importare
          di nuovo.
        </Message>
        <Message v-if="erroreAnnullamento" severity="error" @close="erroreAnnullamento = null">
          <span class="errore-annullamento">{{ erroreAnnullamento }}</span>
        </Message>
        <DataTable :value="associazioni" responsiveLayout="scroll" class="p-datatable-sm">
          <template #empty>Nessuna associazione fatta con questo pulsante.</template>
          <Column header="Associata il">
            <template #body="{ data: associazione }">{{ dataOra(associazione.associata_il) }}</template>
          </Column>
          <Column field="associata_da" header="Da" />
          <Column header="Data contabile">
            <template #body="{ data: associazione }">{{ data(associazione.data_contabile) }}</template>
          </Column>
          <Column field="accrediti" header="Accrediti" />
          <Column field="pagamenti" header="Pagamenti" />
          <Column header="Totale">
            <template #body="{ data: associazione }">{{ euro(associazione.totale) }}</template>
          </Column>
          <Column header="Stato">
            <template #body="{ data: associazione }">
              <template v-if="associazione.annullata_il">
                <Tag severity="warning" value="Annullata" />
                <small class="block mt-1">{{ dataOra(associazione.annullata_il) }} da {{ associazione.annullata_da }}</small>
              </template>
              <Tag v-else severity="success" value="Associata" />
            </template>
          </Column>
          <Column>
            <template #body="{ data: associazione }">
              <Button v-if="!associazione.annullata_il" label="Annulla associazione" icon="pi pi-undo"
                class="p-button-text p-button-danger w-auto" @click="chiediAnnullamento(associazione)" />
            </template>
          </Column>
        </DataTable>
      </div>
    </div>

    <template v-else>
      <div class="flex flex-wrap mb-2">
        <p class="m-1 py-1 px-4 border-1 border-500"><small>Bollettini nel file: <b>{{ verifica.righe }}</b></small></p>
        <p class="m-1 py-1 px-4 border-1 border-500"><small>Totale: <b>{{ euro(verifica.totale) }}</b></small></p>
        <p class="m-1 py-1 px-4 border-1 border-500"><small>Combinazioni trovate: <b>{{ verifica.combinazioni.length }}</b></small></p>
      </div>

      <Message v-if="combinazioniDaSistemare.length" severity="error" :closable="false">
        <b>Da sistemare prima di associare</b> (finché resta qualcosa il pulsante "Associa" è disattivato):
        <div v-for="{ numero, accrediti } in combinazioniDaSistemare" :key="numero" class="mt-2">
          <div v-if="verifica.combinazioni.length > 1"><b>Combinazione {{ numero }}</b></div>
          <div v-for="{ movimento, motivi: elenco } in accrediti" :key="movimento.id">
            {{ canale(movimento) }}: {{ elenco.join('; ') }}
          </div>
        </div>
        <div class="mt-2">Le combinazioni e gli accrediti da sistemare sono aperti qui sotto, con le righe in rosso.</div>
      </Message>

      <Message severity="info" :closable="false">
        In "Da registrare" c'è l'importo pagato dal cliente (Totale txt). Dove è diverso da quanto restava da pagare
        della fattura la riga è gialla e la nota spiega cosa succederà (pagamento parziale, pagato più del dovuto,
        fattura già pagata).
      </Message>

      <Message v-if="rateDilazione" severity="info" :closable="false">
        <b>{{ rateDilazione }} {{ rateDilazione === 1 ? 'riga è una rata' : 'righe sono rate' }} di dilazione</b> (etichetta blu nella colonna Fattura).
        L'importo di una rata non va solo sulla fattura del bollettino: viene diviso sulle fatture della dilazione
        ancora da pagare, dalla scadenza più vecchia. Ogni fattura riceve il suo saldo per intero e l'ultima il resto;
        se avanza qualcosa va sull'ultima. Con "Associa" viene registrato un pagamento per ogni fattura e ne vengono
        aggiornati pagato e saldo, sui saldi di quel momento. La divisione è nelle Note. Le rate stampate prima del
        25/09/2026 si riconoscono dal codice del bollettino, le altre dal numero della fattura.
      </Message>

      <Message v-if="fattureRipetute.length" severity="warn" :closable="false">
        Fatture presenti più volte nel file, controlla che non siano pagamenti doppi:
        <span v-for="(fattura, i) in fattureRipetute" :key="fattura.numero">{{ i > 0 ? ', ' : '' }}<b>{{ fattura.numero }}</b> ({{ fattura.volte }} volte)</span>
      </Message>

      <Message v-if="!verifica.combinazioni.length" severity="error" :closable="false">
        Nessun gruppo di accrediti del {{ data(verifica.data_contabile) }} ancora da associare corrisponde ai
        {{ verifica.righe }} bollettini del file.
        <template v-if="verifica.accrediti.length">
          Accrediti trovati:
          <span v-for="(accredito, i) in verifica.accrediti" :key="accredito.id">{{ i > 0 ? ', ' : '' }}{{ canale(accredito) }} ({{ accredito.quantita }})</span>.
        </template>
        <template v-else>Non c'è nessun accredito di bollettini da associare in quella data.</template>
      </Message>

      <Message v-if="avvisoCombinazioni" severity="warn" :closable="false">{{ avvisoCombinazioni }}</Message>

      <Accordion :key="apertura.verifica" :multiple="true" :lazy="true" :activeIndex="apertura.combinazioni">
        <AccordionTab v-for="(combinazione, c) in verifica.combinazioni" :key="c">
          <template #header>
            <span>Combinazione {{ c + 1 }} · {{ combinazione.movimenti.length }} accrediti</span>
            <Tag v-if="combinazione.totali_tornano" severity="success" value="Totali corretti" class="ml-3" />
            <Tag v-else severity="danger" value="Totali non corrispondenti" class="ml-3" />
            <Tag v-if="accreditiDaSistemare(combinazione).length" severity="danger" class="ml-2"
              :value="etichettaDaSistemare(combinazione)" />
          </template>

          <Message v-if="combinazione.riordinata" severity="info" :closable="false">
            Nel file i blocchi dei canali sono in un ordine diverso da quello degli accrediti: le righe sono state
            assegnate all'accredito con lo stesso totale. Controlla l'ordine qui sotto.
          </Message>
          <Message v-if="!combinazione.totali_tornano" severity="error" :closable="false">
            In nessun ordine i blocchi del file hanno gli stessi totali degli accrediti: le righe sono state divise
            nell'ordine in cui gli accrediti sono stati importati. Controlla gli accrediti segnati con la ✗.
          </Message>

          <div class="flex flex-column align-items-center mb-2">
            <Button label="ASSOCIA QUESTA COMBINAZIONE" class="p-button-text w-auto"
              :disabled="accreditiDaSistemare(combinazione).length > 0" @click="associa(combinazione)" />
            <small v-if="accreditiDaSistemare(combinazione).length" class="p-error">
              Si attiva quando sono sistemati i punti elencati in alto
            </small>
          </div>

          <Accordion :multiple="true" :lazy="true" :activeIndex="apertura.movimenti[c]">
            <AccordionTab v-for="movimento in combinazione.movimenti" :key="movimento.id">
              <template #header>
                <div class="flex align-items-center w-full">
                  <i class="mr-3" :class="quadra(movimento) ? 'pi pi-check text-green-500' : 'pi pi-times p-error'" />
                  <div>
                    <div>{{ canale(movimento) }} · {{ movimento.quantita }} bollettini</div>
                    <small class="uppercase">
                      Totale txt: {{ euro(movimento.totale_txt) }} · Accredito: {{ euro(movimento.importo) }} ·
                      Da registrare: {{ euro(totaleDaRegistrare(movimento)) }}
                    </small>
                    <small v-for="motivo in motivi(movimento)" :key="motivo" class="block p-error motivo">{{ motivo }}</small>
                  </div>
                </div>
              </template>

              <div class="mb-3">
                <div><b>{{ movimento.descrizione }}</b></div>
                <small>
                  {{ movimento.banca }} · Data valuta {{ data(movimento.data_valuta) }} ·
                  Data contabile {{ data(movimento.data_contabile) }}
                </small>
              </div>

              <DataTable :value="movimento.righe" editMode="cell" responsiveLayout="scroll" :rowClass="classeRiga"
                @cell-edit-complete="modificaCella($event, movimento, combinazione)">
                <Column field="riga" header="Riga" />
                <Column field="numero" header="Fattura">
                  <template #body="{ data: riga }">
                    <span class="text-blue-500 cursor-pointer"
                      v-tooltip.top="{ value: `Codice del bollettino: ${riga.codice}`, disabled: !riga.codice }">{{ riga.numero }}</span>
                    <Tag v-if="riga.occorrenze > 1" severity="warning" :value="`x${riga.occorrenze}`" class="ml-2"
                      v-tooltip.top="`Presente ${riga.occorrenze} volte nel file`" />
                    <div v-if="riga.dilazione">
                      <Tag severity="info" :value="etichettaDilazione(riga)" v-tooltip.top="spiegazioneDilazione(riga)" />
                    </div>
                    <div v-if="mostraCodice(riga)" class="codice-originale">Codice: {{ riga.codice }}</div>
                  </template>
                  <template #editor="{ data: riga, field }">
                    <InputText v-model="riga[field]" autofocus />
                  </template>
                </Column>
                <Column field="cliente" header="Cliente">
                  <template #body="{ data: riga }">{{ riga.cliente || 'N/D' }}</template>
                </Column>
                <Column field="importo_txt" header="Totale txt">
                  <template #body="{ data: riga }">{{ euro(riga.importo_txt) }}</template>
                </Column>
                <Column field="importo" header="Da registrare">
                  <template #body="{ data: riga }">
                    <span class="text-blue-500 cursor-pointer">{{ euro(riga.importo) }}</span>
                  </template>
                  <template #editor="{ data: riga, field }">
                    <InputNumber v-model="riga[field]" mode="currency" currency="EUR" locale="it-IT" autofocus />
                  </template>
                </Column>
                <Column field="saldo" header="Saldo fattura">
                  <template #body="{ data: riga }">{{ euro(riga.saldo) }}</template>
                </Column>
                <Column field="pagato" header="Pagato">
                  <template #body="{ data: riga }">{{ euro(riga.pagato) }}</template>
                </Column>
                <Column field="totale_fattura" header="Totale fattura">
                  <template #body="{ data: riga }">{{ euro(riga.totale_fattura) }}</template>
                </Column>
                <Column header="Note" style="min-width: 16rem">
                  <template #body="{ data: riga }">
                    <div v-if="mostraRipartizione(riga)" class="ripartizione">
                      <i class="pi pi-share-alt mr-1" />{{ testoRipartizione(riga) }}
                    </div>
                    <div v-for="segnalazione in riga.segnalazioni" :key="segnalazione.tipo" class="segnalazione"
                      :class="{ bloccante: segnalazione.bloccante }">
                      <i class="pi mr-1" :class="segnalazione.bloccante ? 'pi-ban' : 'pi-exclamation-triangle'" />{{ segnalazione.messaggio }}
                    </div>
                  </template>
                </Column>
                <Column style="width: 3rem">
                  <template #body="{ index }">
                    <i class="pi pi-trash p-error cursor-pointer" @click="rimuoviRiga(movimento, index, combinazione)" />
                  </template>
                </Column>
              </DataTable>

              <div class="flex justify-content-end mt-2">
                <Button icon="pi pi-plus" class="p-button-rounded w-auto" v-tooltip.top="'Aggiungi riga'"
                  @click="aggiungiRiga(movimento)" />
              </div>
            </AccordionTab>
          </Accordion>
        </AccordionTab>
      </Accordion>
    </template>

    <template #footer>
      <Button label="Annulla" icon="pi pi-times" class="p-button-text" @click="chiudi(false)" />
      <Button label="Verifica" icon="pi pi-check" class="p-button-text" :disabled="!file" @click="verificaFile" />
    </template>
  </Dialog>
</template>

<script>
import { ref, computed, getCurrentInstance } from 'vue'
import moment from 'moment'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import Service from '@/services/ask-pratiche/pratiche-service'
import Loader from '@/components/Loader.vue'

const SENZA_FATTURA = { trovata: false, cliente: null, totale_fattura: 0, pagato: 0, saldo: 0, dilazione: null, ripartizione: [] }
const NUMERO_MANCANTE = { tipo: 'fattura_inesistente', messaggio: 'Scrivi il numero della fattura', bloccante: true }
// Note che dipendono dagli importi delle altre righe: dopo una correzione si ricalcolano su tutte le righe toccate
const NOTE_DEGLI_IMPORTI = ['importo_diverso', 'rata_diversa']

const somma = (righe, campo) => Math.round(righe.reduce((totale, riga) => totale + Number(riga[campo] || 0), 0) * 100) / 100
const uguali = (a, b) => Math.abs(Number(a) - Number(b)) < 0.005
const stessoNumero = (a, b) => String(a || '').trim().toUpperCase() === String(b || '').trim().toUpperCase()
const indiciDove = (elenco, condizione) => elenco.reduce((indici, elemento, i) => (condizione(elemento) ? [...indici, i] : indici), [])

// Righe dei bollettini del txt di Poste, con il numero di riga nel file (intestazione e totali ne restano fuori)
const leggiRighe = (testo) => testo.split(/\r?\n/)
  .map((linea, indice) => ({ riga: indice + 1, campi: linea.trim().split(';') }))
  .filter(({ campi }) => campi.length > 14)

export default {
  components: { Loader },
  props: {
    visible: { type: Boolean, default: false },
    tipoPagamento: { type: String, default: '' },
  },
  emits: ['update:visible', 'associated'],
  setup(props, { emit }) {
    const { $http, $eventBus } = getCurrentInstance().appContext.config.globalProperties
    const service = new Service($http, $eventBus)
    const toast = useToast()
    const confirm = useConfirm()

    const file = ref(null)
    const verifica = ref(null)
    // Indici delle combinazioni e degli accrediti aperti dopo la verifica. Le schede annidate di PrimeVue non
    // ridisegnano il contenuto quando cambiano i dati: a ogni verifica si ricreano, con verifica come chiave.
    const apertura = ref({ verifica: 0, combinazioni: [], movimenti: [] })
    const loading = ref(false)
    const associazioni = ref([])
    const erroreAnnullamento = ref(null)

    const avviso = (detail, severity = 'warn') => toast.add({ severity, summary: 'Attenzione', detail, life: 5000 })
    const euro = (valore) => Number(valore || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })
    const data = (valore) => (valore ? moment(valore).format('DD/MM/YYYY') : '')
    const dataOra = (valore) => (valore ? moment(valore).format('DD/MM/YYYY HH:mm') : '')
    const canale = (accredito) => {
      const trovato = accredito.descrizione.match(/\/\s*(.*?)\s*ACCREDITO/)
      return trovato ? trovato[1].replace(/\s+/g, ' ') : accredito.descrizione
    }

    const totaleDaRegistrare = (movimento) => somma(movimento.righe, 'importo')
    const quadra = (movimento) => uguali(movimento.totale_txt, movimento.importo)
      && uguali(totaleDaRegistrare(movimento), movimento.importo)

    // Una riga si può associare solo se la sua fattura esiste
    const daCorreggere = (righe) => righe.filter((riga) => !riga.trovata)
    const elencoRighe = (righe) => righe.map((riga) => riga.riga || 'aggiunta a mano').join(', ')
    const nelleRighe = (righe) => `${righe.length === 1 ? 'nella riga' : 'nelle righe'} ${elencoRighe(righe)}`

    // Perché un accredito non si può ancora associare: finché ce n'è uno "Associa" resta disattivato
    const motivi = (movimento) => {
      const elenco = []
      const senzaFattura = daCorreggere(movimento.righe)
      if (senzaFattura.length) elenco.push(`fattura inesistente ${nelleRighe(senzaFattura)} (il motivo è nelle Note)`)
      const senzaImporto = movimento.righe.filter((riga) => Number(riga.importo || 0) < 0.005)
      if (senzaImporto.length) elenco.push(`importo da registrare a 0 o negativo ${nelleRighe(senzaImporto)}`)
      if (!uguali(movimento.totale_txt, movimento.importo)) {
        elenco.push(`totale del file ${euro(movimento.totale_txt)} diverso dall'accredito ${euro(movimento.importo)}`)
      } else if (!uguali(totaleDaRegistrare(movimento), movimento.importo)) {
        elenco.push(`totale da registrare ${euro(totaleDaRegistrare(movimento))} diverso dall'accredito ${euro(movimento.importo)}`)
      }
      return elenco
    }
    const accreditiDaSistemare = (combinazione) => combinazione.movimenti
      .map((movimento) => ({ movimento, motivi: motivi(movimento) }))
      .filter((accredito) => accredito.motivi.length)
    const etichettaDaSistemare = (combinazione) => {
      const quanti = accreditiDaSistemare(combinazione).length
      return `${quanti} ${quanti === 1 ? 'accredito' : 'accrediti'} da sistemare`
    }
    const combinazioniDaSistemare = computed(() => (verifica.value ? verifica.value.combinazioni : [])
      .map((combinazione, c) => ({ numero: c + 1, accrediti: accreditiDaSistemare(combinazione) }))
      .filter((combinazione) => combinazione.accrediti.length))

    // Fatture ripetute e rate si contano sulle righe delle combinazioni, così seguono le correzioni dell'operatore
    const righeDi = (combinazione) => combinazione.movimenti.flatMap((movimento) => movimento.righe)
    const fattureRipetute = computed(() => {
      if (!verifica.value.combinazioni.length) return verifica.value.fatture_ripetute
      const ripetute = new Map()
      verifica.value.combinazioni.forEach((combinazione) => {
        const volte = new Map()
        righeDi(combinazione).forEach((riga) => {
          const numero = String(riga.numero || '').trim().toUpperCase()
          if (numero) volte.set(numero, (volte.get(numero) || 0) + 1)
        })
        volte.forEach((n, numero) => {
          if (n > 1) ripetute.set(numero, Math.max(ripetute.get(numero) || 0, n))
        })
      })
      return [...ripetute].map(([numero, volte]) => ({ numero, volte }))
    })
    const rateDilazione = computed(() => (verifica.value.combinazioni.length
      ? Math.max(...verifica.value.combinazioni.map((combinazione) => righeDi(combinazione).filter((riga) => riga.dilazione).length))
      : verifica.value.rate_dilazione))

    // Con molti accrediti nello stesso giorno arrivano solo alcune combinazioni: si dice quali
    const avvisoCombinazioni = computed(() => {
      const { combinazioni, combinazioni_trovate: trovate, combinazioni_stesso_totale: stessoTotale } = verifica.value
      if (trovate <= combinazioni.length) return null
      const gruppi = `Gruppi di accrediti con lo stesso numero di bollettini del file: ${trovate}`
      if (stessoTotale > 0 && stessoTotale < trovate) {
        return `${gruppi}, con lo stesso totale: ${stessoTotale}. Sono mostrati solo questi`
          + `${stessoTotale > combinazioni.length ? `, i primi ${combinazioni.length}` : ''}.`
      }
      return `${gruppi}. Sono mostrati i primi ${combinazioni.length}.`
    })

    const classeRiga = (riga) => {
      if (!riga.trovata || !uguali(riga.importo, riga.importo_txt)) return 'bg-pink-100'
      if (riga.occorrenze > 1 || riga.segnalazioni.length) return 'bg-yellow-100'
      if (riga.dilazione) return 'bg-blue-100'
      return null
    }

    // Il numero della rata c'è solo nel codice dei bollettini stampati prima del 25/09/2026
    const rataDelCodice = (riga) => (riga.rata_codice && riga.rata_codice.dilazione === riga.dilazione.id ? riga.rata_codice.rata : null)
    const etichettaDilazione = (riga) => {
      const rata = rataDelCodice(riga)
      if (!rata) return `Dilazione ${riga.dilazione.id}`
      return `Rata ${riga.dilazione.rate ? `${rata}/${riga.dilazione.rate}` : rata} · dilazione ${riga.dilazione.id}`
    }
    const spiegazioneDilazione = (riga) => (rataDelCodice(riga)
      ? 'Rata riconosciuta dal codice del bollettino: l\'importo si divide sulle fatture della dilazione'
      : 'Fattura in dilazione: l\'importo si divide sulle fatture della dilazione')

    // La divisione si mostra quando l'importo non va tutto sulla fattura della riga
    const mostraRipartizione = (riga) => Boolean(riga.ripartizione && riga.ripartizione.length)
      && (riga.ripartizione.length > 1 || riga.ripartizione[0].numero !== riga.numero)
    const testoRipartizione = (riga) => `${riga.ripartizione.length > 1 ? 'Divisa su' : 'Registrata su'}: `
      + riga.ripartizione.map((quota) => `${quota.numero} ${euro(quota.importo)}`).join(' · ')

    // Il codice originale serve quando il problema è il numero: fattura non trovata o codice ambiguo
    const mostraCodice = (riga) => Boolean(riga.codice) && riga.codice !== riga.numero
      && (!riga.trovata || riga.segnalazioni.some((segnalazione) => segnalazione.tipo === 'codice_ambiguo'))

    const scegliFile = (event) => {
      file.value = event.target.files[0] || null
    }

    // Cambia a ogni verifica e alla chiusura: le risposte arrivate dopo non toccano i dati nuovi
    let giro = 0

    const verificaFile = async () => {
      const righe = leggiRighe(await file.value.text())
      if (!righe.length) {
        avviso('Nel file non ci sono righe di bollettini')
        return
      }

      giro += 1
      const questoGiro = giro
      loading.value = true
      const esito = await service.verificaBollettiniTxt(righe)
      loading.value = false
      if (questoGiro !== giro) return
      if (!esito.ok) {
        avviso(esito.message, 'error')
        return
      }
      // Si aprono le combinazioni e gli accrediti da sistemare; dopo li apre e chiude l'operatore
      const movimenti = esito.data.combinazioni.map((combinazione) => indiciDove(combinazione.movimenti, (movimento) => motivi(movimento).length > 0))
      apertura.value = { verifica: apertura.value.verifica + 1, combinazioni: indiciDove(movimenti, (indici) => indici.length > 0), movimenti }
      verifica.value = esito.data
    }

    // Dopo una correzione si ricontrollano subito le righe che ne dipendono: quelle con le fatture indicate,
    // perché le note guardano tutti i bollettini della stessa fattura, e le rate di dilazione, che si dividono
    // una dopo l'altra. La riga col numero cambiato prende tutte le note, le altre tengono quelle del codice.
    const eseguiRicontrollo = async (combinazione, numeri, rigaCambiata) => {
      const questoGiro = giro
      const daRicontrollare = righeDi(combinazione)
        .filter((riga) => String(riga.numero || '').trim() && (riga === rigaCambiata || riga.dilazione
          || numeri.some((numero) => stessoNumero(numero, riga.numero))))
      if (!daRicontrollare.length) return

      const esito = await service.ricontrollaBollettiniTxt(daRicontrollare.map(({ numero, importo, rata_codice }) => ({
        numero, importo: Number(importo || 0), rata_codice,
      })))
      if (!esito.ok || questoGiro !== giro) return
      esito.data.forEach((dati, i) => {
        const riga = daRicontrollare[i]
        const segnalazioni = riga === rigaCambiata ? dati.segnalazioni : [
          ...riga.segnalazioni.filter((segnalazione) => !NOTE_DEGLI_IMPORTI.includes(segnalazione.tipo)),
          ...dati.segnalazioni.filter((segnalazione) => NOTE_DEGLI_IMPORTI.includes(segnalazione.tipo)),
        ]
        const { numero, trovata, cliente, totale_fattura, pagato, saldo, occorrenze, dilazione, ripartizione } = dati
        Object.assign(riga, { numero, trovata, cliente, totale_fattura, pagato, saldo, occorrenze, dilazione, ripartizione, segnalazioni })
      })
    }

    // Un ricontrollo alla volta, nell'ordine delle correzioni: ognuno parte dalle righe come le ha lasciate il precedente
    let ricontrolli = Promise.resolve()
    const ricontrolla = (combinazione, numeri, rigaCambiata = null) => {
      ricontrolli = ricontrolli.catch(() => {}).then(() => eseguiRicontrollo(combinazione, numeri, rigaCambiata))
      return ricontrolli
    }

    // Il valore si scrive sulla riga della tabella e non sulla copia di PrimeVue (newData), che è stata fatta quando
    // la riga è entrata in modifica: sostituendo la riga si perderebbero i ricontrolli e le correzioni fatte dopo.
    // Uscire da una cella senza cambiarla non ricontrolla niente.
    const modificaCella = async ({ data: riga, field, value, newValue }, movimento, combinazione) => {
      // Riga cancellata mentre la cella era aperta
      if (!movimento.righe.includes(riga)) return
      riga[field] = field === 'importo' && newValue === null ? 0 : newValue
      if (field === 'numero' && !stessoNumero(value, newValue)) {
        if (!String(riga.numero || '').trim()) {
          Object.assign(riga, { ...SENZA_FATTURA, occorrenze: 1, segnalazioni: [NUMERO_MANCANTE] })
        }
        await ricontrolla(combinazione, [value, riga.numero], riga)
      } else if (field === 'importo' && !uguali(value, riga.importo)) {
        await ricontrolla(combinazione, [riga.numero])
      }
    }

    const aggiungiRiga = (movimento) => {
      movimento.righe.push({
        ...SENZA_FATTURA, riga: null, numero: '', rata_codice: null, occorrenze: 1, importo_txt: 0, importo: 0,
        segnalazioni: [NUMERO_MANCANTE],
      })
    }

    const rimuoviRiga = async (movimento, index, combinazione) => {
      const [rimossa] = movimento.righe.splice(index, 1)
      await ricontrolla(combinazione, [rimossa.numero])
    }

    const associa = async (combinazione) => {
      // Una cella ancora aperta si chiude con lo stesso clic, dopo questo: si aspetta che il suo valore
      // sia scritto e che i ricontrolli siano finiti
      await new Promise((fatto) => setTimeout(fatto))
      await ricontrolli.catch(() => {})
      if (accreditiDaSistemare(combinazione).length) {
        avviso('Prima di associare vanno sistemati i punti elencati in alto')
        return
      }

      loading.value = true
      const esito = await service.associaBollettiniTxt(combinazione.movimenti.map((movimento) => ({
        id: movimento.id,
        righe: movimento.righe.map(({ numero, importo }) => ({ numero, importo })),
      })))
      loading.value = false
      if (!esito.ok) {
        avviso(esito.message, 'error')
        return
      }

      toast.add({
        severity: 'success',
        summary: 'Bollettini associati',
        detail: `${esito.data.pagamenti} pagamenti registrati su ${esito.data.movimenti} accrediti`,
        life: 5000,
      })
      emit('associated')
      chiudi(false)
    }

    const caricaAssociazioni = async () => {
      const esito = await service.associazioniBollettiniTxt()
      if (esito.ok) associazioni.value = esito.data
    }

    const annullaAssociazione = async (associazione) => {
      loading.value = true
      const esito = await service.annullaAssociazioneBollettiniTxt(associazione.id)
      loading.value = false
      if (!esito.ok) {
        erroreAnnullamento.value = esito.message
        return
      }

      erroreAnnullamento.value = null
      const { pagamenti, accrediti, saldi_ricalcolati: ricalcolati } = esito.data
      toast.add({
        severity: 'success',
        summary: 'Associazione annullata',
        detail: `Pagamenti cancellati: ${pagamenti}. Accrediti di nuovo da associare: ${accrediti}.`
          + (ricalcolati ? ` Saldi ricalcolati per pagamenti arrivati dopo: ${ricalcolati}.` : ''),
        life: 8000,
      })
      await caricaAssociazioni()
      emit('associated')
    }

    const chiediAnnullamento = (associazione) => {
      confirm.require({
        header: 'Annulla associazione',
        icon: 'pi pi-exclamation-triangle',
        message: `Annullare l'associazione del ${dataOra(associazione.associata_il)}? `
          + `Pagamenti da cancellare: ${associazione.pagamenti} (${euro(associazione.totale)}). `
          + `Accrediti che tornano da associare: ${associazione.accrediti}.`,
        acceptLabel: 'Sì, annulla',
        rejectLabel: 'No',
        accept: () => annullaAssociazione(associazione),
      })
    }

    const chiudi = (valore) => emit('update:visible', valore)

    const reset = () => {
      giro += 1
      file.value = null
      verifica.value = null
      erroreAnnullamento.value = null
    }

    return {
      file, verifica, apertura, loading, associazioni, erroreAnnullamento,
      euro, data, dataOra, canale, totaleDaRegistrare, quadra, classeRiga, mostraCodice,
      etichettaDilazione, spiegazioneDilazione, mostraRipartizione, testoRipartizione,
      motivi, accreditiDaSistemare, etichettaDaSistemare, combinazioniDaSistemare,
      fattureRipetute, rateDilazione, avvisoCombinazioni,
      scegliFile, verificaFile, modificaCella, aggiungiRiga, rimuoviRiga, associa,
      caricaAssociazioni, chiediAnnullamento, chiudi, reset,
    }
  },
}
</script>

<style scoped>
.segnalazione {
  font-size: 0.85rem;
  color: #8a5300;
}

.segnalazione.bloccante {
  color: #b32b23;
}

.motivo::first-letter {
  text-transform: uppercase;
}

.ripartizione {
  font-size: 0.85rem;
  color: #1c5f9c;
}

.errore-annullamento {
  white-space: pre-line;
}

.codice-originale {
  font-size: 0.75rem;
  color: #6c757d;
}
</style>
